'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { findCrossedPortal, placeBehindViewer } from '../_pure/portalCrossing.js';
import { selectFocusedPortal } from '../_pure/portalPreview.js';
import { PortalLiveView } from './PortalLiveView.js';
const RIM = 0.07;
const RIM_DEPTH = 0.1;
/** How far the image sits behind the opening, giving it stereo depth. */
const RECESS = 0.35;
const RETURN_PORTAL = { width: 1.2, height: 2, distance: 1.8, drop: 0.6, color: '#7fd1ff' };
/** The focused doorway's rim brightens and a soft halo breathes around it. */
const FOCUS = { rimLighten: 0.45, haloMargin: 0.3, haloOpacity: 0.85, pulseHz: 0.6 };
/** A band around the rim whose colour fades to black outward, so additive blending gives a soft glow. */
function haloGeometry(width, height, color) {
    const iw = width / 2 + RIM;
    const ih = height / 2 + RIM;
    const ow = iw + FOCUS.haloMargin;
    const oh = ih + FOCUS.haloMargin;
    const positions = [-iw, -ih, iw, -ih, iw, ih, -iw, ih, -ow, -oh, ow, -oh, ow, oh, -ow, oh].flatMap((value, index) => index % 2 === 1 ? [value, 0] : [value]);
    const inner = new THREE.Color(color);
    const colors = Array.from({ length: 8 }, (_, index) => index < 4 ? inner.toArray() : [0, 0, 0]).flat();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    // Each side is a quad from inner edge (i, i+1) to outer edge (i+4, i+5), wound to face +Z.
    geometry.setIndex([0, 4, 5, 0, 5, 1, 1, 5, 6, 1, 6, 2, 2, 6, 7, 2, 7, 3, 3, 7, 4, 3, 4, 0]);
    return geometry;
}
function FocusHalo(props) {
    const geometry = useMemo(() => haloGeometry(props.width, props.height, props.color), [props.width, props.height, props.color]);
    useEffect(() => () => geometry.dispose(), [geometry]);
    const material = useRef(null);
    useFrame(({ clock }) => {
        if (!material.current)
            return;
        const pulse = 0.5 + 0.5 * Math.sin(clock.elapsedTime * Math.PI * 2 * FOCUS.pulseHz);
        material.current.opacity = FOCUS.haloOpacity * (0.55 + 0.45 * pulse);
    });
    return (_jsx("mesh", { geometry: geometry, position: [0, 0, RIM_DEPTH / 2 + 0.002], children: _jsx("meshBasicMaterial", { ref: material, vertexColors: true, transparent: true, opacity: FOCUS.haloOpacity, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, side: THREE.DoubleSide }) }));
}
const textureCache = new Map();
function loadTexture(url) {
    let pending = textureCache.get(url);
    if (!pending) {
        pending = new THREE.TextureLoader().loadAsync(url).then((texture) => {
            texture.colorSpace = THREE.SRGBColorSpace;
            return texture;
        });
        pending.catch(() => textureCache.delete(url));
        textureCache.set(url, pending);
    }
    return pending;
}
/** Crops the texture like CSS `cover` so the image fills the opening without stretching. */
function coverTexture(source, aspect) {
    const image = source.image;
    const imageAspect = image?.width && image.height ? image.width / image.height : aspect;
    const texture = source.clone();
    if (imageAspect > aspect) {
        texture.repeat.set(aspect / imageAspect, 1);
        texture.offset.set((1 - texture.repeat.x) / 2, 0);
    }
    else {
        texture.repeat.set(1, imageAspect / aspect);
        texture.offset.set(0, (1 - texture.repeat.y) / 2);
    }
    texture.needsUpdate = true;
    return texture;
}
function useSnapshot(url, aspect) {
    const [texture, setTexture] = useState();
    useEffect(() => {
        if (!url)
            return;
        let live = true;
        let cropped;
        loadTexture(url).then((source) => {
            if (!live)
                return;
            cropped = coverTexture(source, aspect);
            setTexture(cropped);
        }, (error) => console.warn(`[Portal] Snapshot ${url} failed to load:`, error));
        return () => {
            live = false;
            cropped?.dispose();
            setTexture(undefined);
        };
    }, [url, aspect]);
    return texture;
}
function rimGeometry(width, height) {
    const outer = new THREE.Shape();
    const w = width / 2 + RIM;
    const h = height / 2 + RIM;
    outer.moveTo(-w, -h).lineTo(w, -h).lineTo(w, h).lineTo(-w, h).closePath();
    const hole = new THREE.Path();
    hole.moveTo(-width / 2, -height / 2).lineTo(-width / 2, height / 2).lineTo(width / 2, height / 2)
        .lineTo(width / 2, -height / 2).closePath();
    outer.holes.push(hole);
    const geometry = new THREE.ExtrudeGeometry(outer, { depth: RIM_DEPTH, bevelEnabled: false });
    geometry.translate(0, 0, -RIM_DEPTH / 2);
    return geometry;
}
function PortalDoorway({ portal, focused }) {
    const { width, height } = portal;
    const rim = useMemo(() => rimGeometry(width, height), [width, height]);
    useEffect(() => () => rim.dispose(), [rim]);
    const snapshot = useSnapshot(portal.imageUrl, width / height);
    const tint = useMemo(() => new THREE.Color(portal.color).multiplyScalar(0.18), [portal.color]);
    const rimColor = useMemo(() => focused ? new THREE.Color(portal.color).lerp(new THREE.Color('#ffffff'), FOCUS.rimLighten)
        : new THREE.Color(portal.color), [portal.color, focused]);
    return (_jsxs("group", { position: portal.center, rotation: [0, Math.atan2(portal.facing[0], portal.facing[2]), 0], children: [_jsx("mesh", { geometry: rim, children: _jsx("meshBasicMaterial", { color: rimColor, toneMapped: false }) }), focused ? _jsx(FocusHalo, { width: width, height: height, color: portal.color }) : null, _jsxs("mesh", { position: [0, 0, -RECESS / 2], children: [_jsx("boxGeometry", { args: [width, height, RECESS] }), _jsx("meshBasicMaterial", { color: tint, side: THREE.BackSide })] }), _jsxs("mesh", { position: [0, 0, -RECESS + 0.005], children: [_jsx("planeGeometry", { args: [width, height] }), snapshot
                        ? _jsx("meshBasicMaterial", { map: snapshot, toneMapped: false }, "snapshot")
                        : _jsx("meshBasicMaterial", { color: portal.color, transparent: true, opacity: 0.35 }, "glow")] }), _jsxs("mesh", { children: [_jsx("planeGeometry", { args: [width, height] }), _jsx("meshBasicMaterial", { color: portal.color, transparent: true, opacity: 0.08, depthWrite: false, blending: THREE.AdditiveBlending })] }), _jsxs("mesh", { position: [0, 0, -RECESS - 0.005], rotation: [0, Math.PI, 0], children: [_jsx("planeGeometry", { args: [width + 2 * RIM, height + 2 * RIM] }), _jsx("meshBasicMaterial", { color: tint })] }), _jsx(Text, { position: [0, height / 2 + RIM + 0.08, 0], fontSize: focused ? 0.13 : 0.11, maxWidth: width + 0.6, anchorX: "center", anchorY: "bottom", textAlign: "center", color: focused ? '#ffffff' : '#e8eefc', outlineWidth: 0.004, outlineColor: "#070b16", children: portal.title })] }));
}
/**
 * Renders doorways and reports the first one the camera moves through from its front side. With
 * onFocus, it also reports the nearest portal the viewer stands in front of, for a live preview.
 */
export function PortalLayer(props) {
    const previous = useRef(undefined);
    const entered = useRef(false);
    const focusKey = useRef(undefined);
    const onEnter = useRef(props.onEnter);
    onEnter.current = props.onEnter;
    const onFocus = useRef(props.onFocus);
    onFocus.current = props.onFocus;
    const head = useMemo(() => new THREE.Vector3(), []);
    const [focusedKey, setFocusedKey] = useState();
    useEffect(() => {
        previous.current = undefined;
        entered.current = false;
    }, [props.portals]);
    useEffect(() => () => {
        if (focusKey.current !== undefined)
            onFocus.current?.(undefined);
        focusKey.current = undefined;
        setFocusedKey(undefined);
    }, [props.portals]);
    useFrame(({ camera }) => {
        if (entered.current)
            return;
        const current = camera.getWorldPosition(head).toArray();
        const focused = selectFocusedPortal(props.portals, current, focusKey.current);
        if (focused?.portalKey !== focusKey.current) {
            focusKey.current = focused?.portalKey;
            setFocusedKey(focused?.portalKey);
            onFocus.current?.(focused);
        }
        const from = previous.current;
        previous.current = current;
        if (!from)
            return;
        const portal = findCrossedPortal(props.portals, from, current);
        if (!portal)
            return;
        entered.current = true;
        onEnter.current(portal.destination);
    });
    const previewed = props.preview && props.portals.find((portal) => portal.portalKey === props.preview.portalKey);
    return (_jsxs(_Fragment, { children: [props.portals.map((portal) => _jsx(PortalDoorway, { portal: portal, focused: portal.portalKey === focusedKey }, portal.portalKey)), previewed ? _jsx(PortalLiveView, { portal: previewed, preview: props.preview }, previewed.portalKey) : null] }));
}
/** In XR, a doorway behind the viewer's arrival point; it moves only when the viewer is placed anew. */
export function ReturnPortal(props) {
    const [arrival, setArrival] = useState();
    useFrame(() => {
        const next = props.controller.readXrArrival?.();
        if (next?.serial !== arrival?.serial)
            setArrival(next);
    });
    const { destination, title, color } = props.spec;
    const portals = useMemo(() => {
        if (!arrival)
            return [];
        const placement = placeBehindViewer(arrival.eye, arrival.forward, RETURN_PORTAL.distance, RETURN_PORTAL.drop);
        return [{
                portalKey: `return:${arrival.serial}`, destination, title, ...placement,
                width: RETURN_PORTAL.width, height: RETURN_PORTAL.height, color: color ?? RETURN_PORTAL.color,
            }];
    }, [arrival, destination, title, color]);
    return portals.length ? _jsx(PortalLayer, { portals: portals, onEnter: props.onEnter }) : null;
}
